import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["field", "submitButton", "errorMessage"]

  connect() {
    // Server-rendered YES radios with `checked` never fire a change event, so
    // dependent policy/details fields would stay in the default non-required
    // styling even when prefilled. Replay the toggle once so their required
    // state and green/red styling matches a freshly-clicked YES.
    this.element.querySelectorAll('input[type="radio"][value="yes"]:checked').forEach(radio => {
      this.toggleDependentFields({ target: radio })
    })
    this.validateAll()
    // Listen for signature pad changes
    this.element.addEventListener("signature-changed", () => this.updateSubmitState())
  }

  // Called on field input
  fieldChanged(event) {
    this.validateField(event.target, true) // Update styling
    this.updateSubmitState()
  }

  // Called on field blur
  fieldBlurred(event) {
    const isValid = this.validateField(event.target, true) // Update styling
    if (!isValid) {
      this.showFieldError(event.target)
    }
  }

  validateField(field, updateStyling = false) {
    let isRequired = field.dataset.required === "true" || field.hasAttribute("required")

    const type = field.type

    // For radio buttons, check if ANY radio in the group is required
    if (type === "radio") {
      const radios = this.element.querySelectorAll(`input[type="radio"][name="${field.name}"]`)
      isRequired = Array.from(radios).some(r => r.dataset.required === "true" || r.hasAttribute("required"))
    }

    if (!isRequired) {
      return true
    }

    let isValid = false

    switch (type) {
      case "checkbox":
        isValid = field.checked
        break
      case "radio":
        // For radio groups, check if any in the group is selected
        const radios = this.element.querySelectorAll(`input[type="radio"][name="${field.name}"]`)
        isValid = Array.from(radios).some(r => r.checked)
        break
      case "date":
        isValid = field.value.trim() !== ""
        break
      default:
        isValid = field.value.trim() !== ""
    }

    // Update styling if requested
    if (updateStyling) {
      this.updateFieldStyling(field, isValid)
    }

    return isValid
  }

  updateFieldStyling(field, isValid) {
    // For radio buttons, check if the GROUP is required (any radio in the group has required)
    let isRequired = field.dataset.required === "true" || field.hasAttribute("required")

    // For radio buttons, check if any radio in the group is required
    if (field.type === "radio") {
      const radios = this.element.querySelectorAll(`input[type="radio"][name="${field.name}"]`)
      isRequired = Array.from(radios).some(r => r.dataset.required === "true" || r.hasAttribute("required"))
    }

    if (!isRequired) return

    // Find the colored wrapper around the field (radio-group span, required
    // checkbox span, etc.). Searching from parentElement keeps closest() from
    // matching the field itself once it picks up bg-red-50/bg-green-50 on a
    // prior validation pass — that match would short-circuit `wrapper !== field`
    // and leave the real wrapper stuck on the wrong colour.
    const wrapper = field.parentElement?.closest(".bg-red-50, .bg-green-50, [data-radio-group]") || null

    if (isValid) {
      // Valid - green styling
      field.classList.remove("border-red-500", "bg-red-50", "ring-red-500/20")
      field.classList.add("border-green-500", "bg-green-50")
      if (wrapper && wrapper !== field) {
        wrapper.classList.remove("bg-red-50", "border-red-500")
        wrapper.classList.add("bg-green-50", "border-green-500")
      }
      // Remove error message
      const errorId = `${field.name}-error`
      const errorEl = document.getElementById(errorId)
      if (errorEl) errorEl.remove()
    } else {
      // Invalid - red styling
      field.classList.remove("border-green-500", "bg-green-50")
      field.classList.add("border-red-500", "bg-red-50")
      if (wrapper && wrapper !== field) {
        wrapper.classList.remove("bg-green-50", "border-green-500")
        wrapper.classList.add("bg-red-50", "border-red-500")
      }
    }
  }

  validateAll() {
    let allValid = true

    this.fieldTargets.forEach(field => {
      if (!this.validateField(field, true)) {
        allValid = false
      }
    })

    return allValid
  }

  showFieldError(field) {
    const isRequired = field.dataset.required === "true" || field.hasAttribute("required")
    if (!isRequired) return

    const isValid = this.validateField(field)
    if (isValid) return

    // Add error styling
    field.classList.add("border-red-500", "ring-red-500/20", "bg-red-50")
    field.classList.remove("border-border", "border-green-500", "bg-green-50")

    // Also update parent wrapper if it exists (for checkboxes/radios)
    const wrapper = field.closest(".bg-green-50, .border-green-500")
    if (wrapper && wrapper !== field) {
      wrapper.classList.remove("bg-green-50", "border-green-500")
      wrapper.classList.add("bg-red-50", "border-red-500")
    }

    // Show inline error message if applicable
    const errorId = `${field.name}-error`
    let errorEl = document.getElementById(errorId)

    if (!errorEl) {
      errorEl = document.createElement("span")
      errorEl.id = errorId
      errorEl.className = "text-xs text-red-600 font-medium mt-1 block"
      field.parentNode.insertBefore(errorEl, field.nextSibling)
    }

    const fieldType = field.type
    if (fieldType === "checkbox") {
      errorEl.textContent = "This field must be checked"
    } else if (fieldType === "radio") {
      errorEl.textContent = "Please select an option"
    } else {
      errorEl.textContent = "This field is required"
    }
  }

  clearFieldError(field) {
    // Remove error message
    const errorId = `${field.name}-error`
    const errorEl = document.getElementById(errorId)
    if (errorEl) {
      errorEl.remove()
    }
  }

  updateSubmitState() {
    const formFieldsValid = this.validateAll()

    // Check if signature pad has a signature
    // The signature pad stores data in a hidden input
    const signatureInput = this.element.querySelector('[data-signature-pad-target="input"]')
    const signatureValid = signatureInput && signatureInput.value.trim() !== ""

    const canSubmit = formFieldsValid && signatureValid

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !canSubmit
    }
  }

  // Validate all required fields and show errors before submission
  validateBeforeSubmit(event) {
    let hasErrors = false

    this.fieldTargets.forEach(field => {
      const isRequired = field.dataset.required === "true" || field.hasAttribute("required")
      if (isRequired && !this.validateField(field)) {
        hasErrors = true
        this.showFieldError(field)
      }
    })

    if (hasErrors) {
      event.preventDefault()
      // Scroll to first error
      const firstError = this.element.querySelector(".border-red-500")
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" })
        firstError.focus()
      }
    }
  }

  // Get all form field values as an object
  getFieldValues() {
    const values = {}

    this.fieldTargets.forEach(field => {
      const name = this.extractFieldName(field.name)
      if (!name) return

      switch (field.type) {
        case "checkbox":
          values[name] = field.checked ? "1" : "0"
          break
        case "radio":
          if (field.checked) {
            values[name] = field.value
          }
          break
        default:
          values[name] = field.value
      }
    })

    return values
  }

  extractFieldName(fullName) {
    // Extract field name from "contract[form_fields][field_name]"
    const match = fullName.match(/\[form_fields\]\[([^\]]+)\]/)
    return match ? match[1] : null
  }

  // Toggle required state of dependent fields based on YES/NO radio selection
  toggleDependentFields(event) {
    const radio = event.target
    const groupName = this.extractFieldName(radio.name)
    if (!groupName) return

    const isYes = radio.value === "yes" && radio.checked

    // Find all fields that depend on this radio group
    const dependentFields = this.element.querySelectorAll(`[data-depends-on="${groupName}"]`)

    dependentFields.forEach(field => {
      if (isYes) {
        // Make field required when YES is selected
        field.dataset.required = "true"
        field.setAttribute("required", "")
        // Add required styling (red border/background)
        field.classList.remove("border-border", "border-green-500", "bg-green-50")
        field.classList.add("border-red-500", "bg-red-50")
      } else {
        // Make field optional when NO is selected
        delete field.dataset.required
        field.removeAttribute("required")
        // Remove required styling, use neutral styling
        field.classList.remove("border-red-500", "bg-red-50", "border-green-500", "bg-green-50")
        field.classList.add("border-border")
        // Clear any error messages
        this.clearFieldError(field)
      }
    })

    // Re-validate to update submit button state
    this.updateSubmitState()
  }
}
